import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import { AuthProvider, useAuth, hasRole } from "./lib/auth";
import { BrandingProvider } from "./lib/branding";
import AppShell from "./components/AppShell";
import { Toaster } from "./components/ui/sonner";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Agencies from "./pages/Agencies";
import Advertisers from "./pages/Advertisers";
import AdAccounts from "./pages/AdAccounts";
import Campaigns from "./pages/Campaigns";
import Schedules from "./pages/Schedules";
import ExportHistory from "./pages/ExportHistory";
import AuditLogs from "./pages/AuditLogs";
import Branding from "./pages/Branding";
import Domains from "./pages/Domains";
import MarginAlerts from "./pages/MarginAlerts";
import Users from "./pages/Users";
import DetailedReports from "./pages/DetailedReports";
import MetaDetailedReports from "./pages/MetaDetailedReports";
import DV360DetailedReports from "./pages/DV360DetailedReports";
import TikTokDetailedReports from "./pages/TikTokDetailedReports";
import Templates from "./pages/Templates";
import EmailSettings from "./pages/EmailSettings";
import DataSync from "./pages/DataSync";
import Alerts from "./pages/Alerts";

function Protected({ children, roles }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-zinc-50">
                <div className="text-zinc-500 text-sm">Loading workspace…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
    if (roles && !hasRole(user, ...roles))
        return <Navigate to="/dashboard" replace />;
    return <AppShell>{children}</AppShell>;
}

function PublicOnly({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Navigate to="/dashboard" replace />;
    return children;
}

function App() {
    return (
        <BrowserRouter>
            <BrandingProvider>
                <AuthProvider>
                    <Routes>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
                        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
                        <Route path="/agencies" element={<Protected roles={["super_admin"]}><Agencies /></Protected>} />
                        <Route path="/advertisers" element={<Protected roles={["super_admin", "admin"]}><Advertisers /></Protected>} />
                        <Route path="/ad-accounts" element={<Protected><AdAccounts /></Protected>} />
                        <Route path="/campaigns" element={<Protected><Campaigns /></Protected>} />
                        <Route path="/schedules" element={<Protected><Schedules /></Protected>} />
                        <Route path="/exports" element={<Protected><ExportHistory /></Protected>} />
                        <Route path="/branding" element={<Protected roles={["super_admin", "admin"]}><Branding /></Protected>} />
                        <Route path="/domains" element={<Protected roles={["super_admin", "admin"]}><Domains /></Protected>} />
                        <Route path="/audit-logs" element={<Protected roles={["super_admin", "admin"]}><AuditLogs /></Protected>} />
                        <Route path="/margin-alerts" element={<Protected roles={["super_admin", "admin"]}><MarginAlerts /></Protected>} />
                        <Route path="/users" element={<Protected roles={["super_admin", "admin"]}><Users /></Protected>} />
                        <Route path="/detailed-reports" element={<Protected><DetailedReports /></Protected>} />
                        <Route path="/detailed-reports/meta" element={<Protected><MetaDetailedReports /></Protected>} />
                        <Route path="/detailed-reports/dv360" element={<Protected><DV360DetailedReports /></Protected>} />
                        <Route path="/detailed-reports/tiktok" element={<Protected><TikTokDetailedReports /></Protected>} />
                        <Route path="/templates" element={<Protected><Templates /></Protected>} />
                        <Route path="/settings/email" element={<Protected><EmailSettings /></Protected>} />
                        <Route path="/data-sync" element={<Protected roles={["super_admin"]}><DataSync /></Protected>} />
                        <Route path="/alerts" element={<Protected><Alerts /></Protected>} />
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                    <Toaster richColors theme="dark" position="top-right" />
                </AuthProvider>
            </BrandingProvider>
        </BrowserRouter>
    );
}

export default App;
